// pages/Wallet.tsx

import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../store/store";
import {
  getBalance,
  getDepositAddress,
  deposit,
  withdraw,
  getTransactions,
  clearError,
  clearTransactionStatus,
} from "../../store/slices/walletSlice";
import Header, {
  translateText,
  getCurrentLang,
} from "../../components/Layout/Header";
import BottomNav from "../../components/Layout/BottomNav";
import Card from "../../components/UI/Card";
import Button from "../../components/UI/Button";
import Modal from "../../components/UI/Modal";
import Drawer from "../../components/UI/Drawer";
import Alert from "../../components/UI/Alert";
import globalStyle from "../../styles/globalStyle";
import walletService, { WithdrawalRules } from "../../services/walletService";
import {
  formatCurrency,
  getXrpPrice,
  setXrpPriceInUSD,
  formatXRP,
} from "../../utils/formatters";

import {
  ArrowUpIcon,
  ArrowDownIcon,
  DocumentDuplicateIcon,
  QrCodeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CurrencyDollarIcon,
  GiftIcon,
  UserGroupIcon,
  ChartBarIcon,
  BanknotesIcon,
  RocketLaunchIcon,
  SparklesIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  CpuChipIcon,
  ShieldCheckIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  TrophyIcon,
  GlobeAltIcon,
} from "@heroicons/react/24/outline";

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// ── Get browser timezone once at module level ─────────────────────────────────
const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// ── Format a short timezone label, e.g. "WAT (Africa/Lagos)" ─────────────────
function tzLabel(tz: string): string {
  try {
    const abbr =
      new Intl.DateTimeFormat("en", {
        timeZoneName: "short",
        timeZone: tz,
      })
        .formatToParts(new Date())
        .find((p) => p.type === "timeZoneName")?.value ?? tz;
    return `${abbr} (${tz})`;
  } catch {
    return tz;
  }
}

const LoadingScreen: React.FC<{ message?: string }> = ({
  message = "Loading wallet data...",
}) => (
  <div className="w-full h-64 bg-black">
    <div className="h-full flex flex-col gap-2 items-center justify-center bg-black/80">
      <svg className={`${globalStyle.loadingSpinner} mr-2`} viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
          fill="none"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
      <div className="text-center">
        <p className="mt-4 text-gray-400">{message}</p>
      </div>
    </div>
  </div>
);

// ── Transaction types ─────────────────────────────────────────────────────────

type TransactionType =
  | "deposit"
  | "withdrawal"
  | "trade_earning"
  | "airdrop"
  | "commission"
  | "bot_earning"
  | "referral_earning"
  | "signup_bonus"
  | "signup_reward"
  | "bonus"
  | "bot_purchase"
  | "bot_capital_return"
  | "team_bonus";

interface Transaction {
  id: number;
  type: TransactionType;
  amount: number;
  status: "pending" | "completed" | "rejected";
  created_at: string;
  destination_address?: string;
  memo?: string;
  tx_hash?: string;
  metadata?: any;
}

// ── Main component ────────────────────────────────────────────────────────────

const Wallet: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    balance,
    depositAddress,
    transactions,
    totalPages,
    currentPage,
    totalItems,
    isLoading,
    depositLoading,
    withdrawalLoading,
    error,
  } = useSelector((state: RootState) => state.wallet);
  const { user } = useSelector((state: RootState) => state.auth);

  const [showDepositModal, setShowDepositModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [showQrModal, setShowQrModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showTransactionDetail, setShowTransactionDetail] =
    useState<Transaction | null>(null);

  const [depositAmount, setDepositAmount] = useState("");
  const [depositTxId, setDepositTxId] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [withdrawAddress, setWithdrawAddress] = useState("");
  const [withdrawMemo, setWithdrawMemo] = useState("");

  // OTP state
  const [withdrawalOtp, setWithdrawalOtp] = useState(["", "", "", "", "", ""]);
  const [otpTimer, setOtpTimer] = useState(0);
  const [pendingWithdrawal, setPendingWithdrawal] = useState<any>(null);
  const [otpInterval, setOtpInterval] = useState<NodeJS.Timeout | null>(null);
  const [isOtpLoading, setIsOtpLoading] = useState(false);

  const [transactionType, setTransactionType] = useState<
    TransactionType | "all"
  >("all");
  const [transactionStatus, setTransactionStatus] = useState<
    "all" | "pending" | "completed" | "rejected"
  >("all");

  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [xrpPrice, setXrpPrice] = useState(getXrpPrice());
  const [priceSource, setPriceSource] = useState<"live" | "cached">("cached");
  const [localPage, setLocalPage] = useState(1);
  const [validationErrors, setValidationErrors] = useState<{
    amount?: string;
    address?: string;
    txId?: string;
    otp?: string;
  }>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // ── Withdrawal Rules ───────────────────────────────────────────────────────
  const [withdrawalRules, setWithdrawalRules] =
    useState<WithdrawalRules | null>(null);
  const [rulesLoading, setRulesLoading] = useState(false);
  const [withdrawalPillLabel, setWithdrawalPillLabel] = useState<string>("");

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    dispatch(getBalance());
    dispatch(getDepositAddress());
    dispatch(getTransactions({ page: localPage }));
    loadWithdrawalRules();
    return () => {
      if (otpInterval) clearInterval(otpInterval);
    };
  }, [dispatch, localPage]); // eslint-disable-line

  const loadWithdrawalRules = async () => {
    try {
      setRulesLoading(true);
      // walletService.getWithdrawalRules() automatically passes BROWSER_TZ
      const rules = await walletService.getWithdrawalRules();
      setWithdrawalRules(rules);
    } catch {
      // fail silently
    } finally {
      setRulesLoading(false);
    }
  };

  useEffect(() => {
    const retrigger = () =>
      setWithdrawalRules((prev) => (prev ? { ...prev } : prev));
    window.addEventListener("storage", retrigger);
    window.addEventListener("regalflux_lang_change", retrigger);
    return () => {
      window.removeEventListener("storage", retrigger);
      window.removeEventListener("regalflux_lang_change", retrigger);
    };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!withdrawalRules) return;
    const buildAndTranslate = async () => {
      const lang = getCurrentLang();
      let english: string;
      if (withdrawalRules.is_allowed_today) {
        english = `Withdrawals open today · ${formatXRP(withdrawalRules.remaining_today)} XRP remaining`;
      } else {
        const today = withdrawalRules.today_dow;
        const sorted = [...withdrawalRules.allowed_days].sort((a, b) => a - b);
        const nextDow = sorted.find((d) => d > today) ?? sorted[0];
        english = `No withdrawals today · Opens ${DAY_NAMES[nextDow] ?? "soon"}`;
      }
      if (lang === "en") {
        setWithdrawalPillLabel(english);
      } else {
        const translated = await translateText(english, lang);
        setWithdrawalPillLabel(translated);
      }
    };
    buildAndTranslate();
  }, [withdrawalRules]); // eslint-disable-line

  useEffect(() => {
    if (balance?.estimated_usd && user?.xrp_balance) {
      const calc = balance.estimated_usd / user.xrp_balance;
      if (calc > 0) {
        setXrpPriceInUSD(calc);
        setXrpPrice(getXrpPrice());
        setPriceSource("live");
      }
    }
  }, [balance, user]);

  useEffect(() => {
    if (depositAddress) {
      const address =
        typeof depositAddress === "string"
          ? depositAddress
          : depositAddress.address;
      if (address)
        setQrCodeUrl(
          `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(address)}`,
        );
    }
  }, [depositAddress]);

  useEffect(() => {
    if (!showDepositModal && !showWithdrawModal && !showOtpModal) {
      dispatch(clearError());
      setValidationErrors({});
      setSuccessMessage(null);
    }
  }, [showDepositModal, showWithdrawModal, showOtpModal, dispatch]);

  // OTP countdown
  useEffect(() => {
    if (otpTimer > 0 && showOtpModal) {
      const interval = setInterval(() => {
        setOtpTimer((prev) => {
          if (prev <= 1) {
            clearInterval(interval);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      setOtpInterval(interval);
      return () => clearInterval(interval);
    }
  }, [otpTimer, showOtpModal]);

  // ── Computed ───────────────────────────────────────────────────────────────

  const isWithdrawalAllowedToday = withdrawalRules?.is_allowed_today ?? true;
  const remainingToday =
    withdrawalRules?.remaining_today ?? withdrawalRules?.daily_limit ?? 50000;
  const effectiveMaxWithdraw = Math.min(
    remainingToday,
    balance?.xrp_balance ?? 0,
  );

  const getNextAllowedDay = (): string => {
    if (!withdrawalRules || withdrawalRules.allowed_days.length === 0)
      return "soon";
    const today = withdrawalRules.today_dow;
    const sorted = [...withdrawalRules.allowed_days].sort((a, b) => a - b);
    const next = sorted.find((d) => d > today) ?? sorted[0];
    return DAY_NAMES[next];
  };

  const getTotalBalanceValue = () => (balance?.xrp_balance || 0) * xrpPrice;

  const formatBalance = (balanceValue: any) => {
    const num =
      typeof balanceValue === "string"
        ? parseFloat(balanceValue)
        : Number(balanceValue ?? 0);
    return isNaN(num) ? "0.00 XRP" : `${formatXRP(num)} XRP`;
  };

  const formatXRPAmount = (amount: any) => {
    const num =
      typeof amount === "string" ? parseFloat(amount) : Number(amount ?? 0);
    return isNaN(num) ? "0.00" : formatXRP(num);
  };

  const getAddressString = () => {
    if (!depositAddress) return "";
    return typeof depositAddress === "string"
      ? depositAddress
      : depositAddress.address || "";
  };

  const getDepositNote = () => {
    if (!depositAddress || typeof depositAddress === "string")
      return "Send XRP to this address and then submit transaction details";
    return (
      depositAddress.note ||
      "Send XRP to this address and then submit transaction details"
    );
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // ── OTP handlers ───────────────────────────────────────────────────────────

  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return;
    const newOtp = [...withdrawalOtp];
    newOtp[index] = value;
    setWithdrawalOtp(newOtp);
    if (value !== "" && index < 5) {
      const next = document.getElementById(`otp-${index + 1}`);
      if (next) next.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && withdrawalOtp[index] === "" && index > 0) {
      const prev = document.getElementById(`otp-${index - 1}`);
      if (prev) prev.focus();
    }
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const validateWithdrawal = () => {
    const errors: typeof validationErrors = {};
    if (!withdrawAmount) {
      errors.amount = "Amount is required";
    } else {
      const amount = parseFloat(withdrawAmount);
      if (isNaN(amount)) errors.amount = "Invalid amount";
      else if (amount < 5) errors.amount = "Minimum withdrawal is 5 XRP";
      else if (amount > (balance?.xrp_balance || 0))
        errors.amount = `Insufficient balance. Available: ${formatXRP(balance?.xrp_balance || 0)} XRP`;
      else if (withdrawalRules && amount > withdrawalRules.remaining_today)
        errors.amount = `Daily limit reached. You can withdraw up to ${formatXRP(withdrawalRules.remaining_today)} XRP more today.`;
    }
    if (!withdrawAddress) errors.address = "Destination address is required";
    else if (!withdrawAddress.startsWith("r") || withdrawAddress.length !== 34)
      errors.address = "Invalid XRP address format";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const validateDeposit = () => {
    const errors: typeof validationErrors = {};
    if (!depositAmount) {
      errors.amount = "Amount is required";
    } else {
      const amount = parseFloat(depositAmount);
      if (isNaN(amount)) errors.amount = "Invalid amount";
      else if (amount < 5) errors.amount = "Minimum deposit is 5 XRP";
      else if (amount > 1000000)
        errors.amount = "Maximum deposit is 1,000,000 XRP";
    }
    if (!depositTxId) errors.txId = "Transaction ID is required";
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Action handlers ────────────────────────────────────────────────────────

  const handleDeposit = async () => {
    if (!validateDeposit()) return;
    const result = await dispatch(
      deposit({ amount: parseFloat(depositAmount), tx_id: depositTxId }),
    );
    if (deposit.fulfilled.match(result)) {
      setShowDepositModal(false);
      setDepositAmount("");
      setDepositTxId("");
      dispatch(getBalance());
      dispatch(getTransactions({ page: localPage }));
      setValidationErrors({});
      setSuccessMessage("Deposit request submitted successfully!");
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  };

  const handleInitiateWithdrawal = async () => {
    if (!validateWithdrawal()) return;
    try {
      // timezone is injected automatically by walletService.initiateWithdrawal
      const response = await walletService.initiateWithdrawal({
        amount: parseFloat(withdrawAmount),
        destination_address: withdrawAddress,
        memo: withdrawMemo || undefined,
      });
      setPendingWithdrawal({
        amount: withdrawAmount,
        address: withdrawAddress,
        memo: withdrawMemo,
      });
      setShowWithdrawModal(false);
      setShowOtpModal(true);
      setOtpTimer(600);
      setWithdrawalOtp(["", "", "", "", "", ""]);
      setValidationErrors({});
      setSuccessMessage(response.message);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error: any) {
      setValidationErrors({
        amount:
          error.response?.data?.message || "Failed to initiate withdrawal",
      });
    }
  };

  const handleConfirmWithdrawal = async () => {
    const otpString = withdrawalOtp.join("");
    if (otpString.length !== 6) {
      setValidationErrors({ otp: "Please enter a valid 6-digit OTP" });
      return;
    }
    setIsOtpLoading(true);
    try {
      const response = await walletService.confirmWithdrawal({
        otp: otpString,
      });
      setShowOtpModal(false);
      setWithdrawAmount("");
      setWithdrawAddress("");
      setWithdrawMemo("");
      setWithdrawalOtp(["", "", "", "", "", ""]);
      setPendingWithdrawal(null);
      setOtpTimer(0);
      dispatch(getBalance());
      dispatch(getTransactions({ page: localPage }));
      loadWithdrawalRules();
      setSuccessMessage(response.message);
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error: any) {
      setValidationErrors({
        otp: error.response?.data?.message || "OTP verification failed",
      });
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleResendOtp = async () => {
    if (otpTimer > 0) return;
    setIsOtpLoading(true);
    try {
      const response = await walletService.resendWithdrawalOtp();
      setOtpTimer(600);
      setWithdrawalOtp(["", "", "", "", "", ""]);
      setValidationErrors({});
      setSuccessMessage(response.message);
      setTimeout(() => setSuccessMessage(null), 5000);
      const firstInput = document.getElementById("otp-0");
      if (firstInput) firstInput.focus();
    } catch (error: any) {
      setValidationErrors({
        otp: error.response?.data?.message || "Failed to resend OTP",
      });
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleCancelWithdrawal = async () => {
    try {
      await walletService.cancelWithdrawal();
    } catch (error: any) {
      console.error("Cancel withdrawal error:", error);
    } finally {
      setShowOtpModal(false);
      setShowWithdrawModal(false);
      setWithdrawAmount("");
      setWithdrawAddress("");
      setWithdrawMemo("");
      setWithdrawalOtp(["", "", "", "", "", ""]);
      setPendingWithdrawal(null);
      setOtpTimer(0);
      setValidationErrors({});
      setSuccessMessage("Withdrawal request cancelled.");
      setTimeout(() => setSuccessMessage(null), 5000);
    }
  };

  const copyToClipboard = (text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setSuccessMessage("Address copied to clipboard!");
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const safeFormatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid Date";
    }
  };

  // ── UI helpers ─────────────────────────────────────────────────────────────

  const getStatusBadge = (status: string) => {
    const base = `${globalStyle.badge} px-2 py-1`;
    switch (status) {
      case "completed":
        return (
          <span className={`${base} ${globalStyle.badgeSuccess}`}>
            Completed
          </span>
        );
      case "pending":
        return (
          <span className={`${base} ${globalStyle.badgeWarning}`}>Pending</span>
        );
      case "rejected":
        return (
          <span className={`${base} ${globalStyle.badgeDanger}`}>Rejected</span>
        );
      default:
        return (
          <span className={`${base} ${globalStyle.badgeInfo}`}>{status}</span>
        );
    }
  };

  const getTypeIcon = (type: TransactionType) => {
    switch (type) {
      case "deposit":
        return <ArrowUpIcon className="w-5 h-5 text-green-500" />;
      case "withdrawal":
        return <ArrowDownIcon className="w-5 h-5 text-red-500" />;
      case "trade_earning":
        return <ChartBarIcon className="w-5 h-5 text-blue-500" />;
      case "airdrop":
        return <GiftIcon className="w-5 h-5 text-purple-500" />;
      case "commission":
        return <CurrencyDollarIcon className="w-5 h-5 text-yellow-500" />;
      case "bot_earning":
        return <CpuChipIcon className="w-5 h-5 text-cyan-500" />;
      case "referral_earning":
        return <UserGroupIcon className="w-5 h-5 text-orange-500" />;
      case "signup_bonus":
      case "signup_reward":
        return <SparklesIcon className="w-5 h-5 text-pink-500" />;
      case "bonus":
        return <GiftIcon className="w-5 h-5 text-emerald-500" />;
      case "bot_purchase":
        return <RocketLaunchIcon className="w-5 h-5 text-indigo-500" />;
      case "bot_capital_return":
        return <ShieldCheckIcon className="w-5 h-5 text-teal-500" />;
      case "team_bonus":
        return <TrophyIcon className="w-5 h-5 text-yellow-400" />;
      default:
        return <BanknotesIcon className="w-5 h-5 text-gray-500" />;
    }
  };

  const getTypeLabel = (type: TransactionType): string => {
    switch (type) {
      case "deposit":
        return "Deposit";
      case "withdrawal":
        return "Withdrawal";
      case "trade_earning":
        return "Trading Earnings";
      case "airdrop":
        return "Airdrop";
      case "commission":
        return "Commission";
      case "bot_earning":
        return "Bot Earnings";
      case "referral_earning":
        return "Referral Earnings";
      case "signup_bonus":
        return "Signup Bonus";
      case "signup_reward":
        return "Signup Reward";
      case "bonus":
        return "Bonus";
      case "bot_purchase":
        return "Bot Purchase";
      case "bot_capital_return":
        return "Capital Return";
      case "team_bonus":
        return "Team Bonus";
      default:
        return type;
    }
  };

  const getTypeColor = (type: TransactionType) => {
    switch (type) {
      case "deposit":
        return "text-green-500";
      case "withdrawal":
        return "text-red-500";
      case "trade_earning":
      case "bot_earning":
      case "commission":
        return "text-blue-500";
      case "airdrop":
      case "signup_bonus":
      case "signup_reward":
      case "bonus":
        return "text-purple-500";
      case "referral_earning":
        return "text-orange-500";
      case "bot_purchase":
        return "text-indigo-500";
      case "bot_capital_return":
        return "text-teal-500";
      case "team_bonus":
        return "text-yellow-400";
      default:
        return "text-gray-500";
    }
  };

  const getTransactionTypeOptions = (): (TransactionType | "all")[] => [
    "all",
    "deposit",
    "withdrawal",
    "trade_earning",
    "airdrop",
    "commission",
    "bot_earning",
    "referral_earning",
    "signup_bonus",
    "signup_reward",
    "bonus",
    "bot_purchase",
    "bot_capital_return",
    "team_bonus",
  ];

  const getTeamBonusSubLabel = (tx: Transaction): string => {
    if (!tx.metadata?.type) return "Team Bonus";
    if (tx.metadata.type === "level_up_bonus")
      return `Tier ${tx.metadata.level ?? "?"} Level-Up Bonus`;
    if (tx.metadata.type === "level_bonus_referrer_cut")
      return `Referrer cut — Tier ${tx.metadata.level ?? "?"} (${tx.metadata.awardee_username ?? "a member"})`;
    return "Team Bonus";
  };

  const filteredTransactions = (transactions || []).filter((transaction) => {
    if (transactionType !== "all" && transaction.type !== transactionType)
      return false;
    if (transactionStatus !== "all" && transaction.status !== transactionStatus)
      return false;
    return true;
  });

  const handlePreviousPage = () => {
    if (currentPage > 1) setLocalPage(currentPage - 1);
  };
  const handleNextPage = () => {
    if (currentPage < totalPages) setLocalPage(currentPage + 1);
  };
  const handleRefresh = () => dispatch(getTransactions({ page: localPage }));

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className={globalStyle.container}>
      <Header />

      <main className="page-content">
        {successMessage && (
          <Alert
            type="success"
            message={successMessage}
            onClose={() => setSuccessMessage(null)}
            className="mb-6"
          />
        )}

        {/* Balance Card */}
        <Card className="mb-6 bg-gradient-to-r from-blue-900 to-purple-900">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <p className={`${globalStyle.body2} text-primary-100 mb-2`}>
                Available Balance
              </p>
              <div className="flex items-baseline">
                <h2 className={`${globalStyle.h2} text-white`}>
                  {isLoading
                    ? "Loading..."
                    : formatBalance(balance?.xrp_balance)}
                </h2>
                <span className="ml-2 text-primary-200">
                  {formatCurrency(getTotalBalanceValue())}
                </span>
              </div>
              <div className="flex items-center mt-2">
                <p className={`${globalStyle.caption} text-primary-200 mr-2`}>
                  1 XRP = {formatCurrency(xrpPrice)}
                </p>
                <span
                  className={`text-xs ${priceSource === "live" ? "text-green-300" : "text-yellow-300"}`}
                >
                  ({priceSource === "live" ? "Live" : "Cached"})
                </span>
              </div>
            </div>
            <div className="flex justify-center space-x-3">
              <Button
                variant="secondary"
                size="lg"
                onClick={() => setShowDepositModal(true)}
                fullWidth
              >
                <ArrowUpIcon className="w-4 h-4 mr-2 text-primary" />
                Deposit
              </Button>
              <Button
                variant="secondary"
                size="lg"
                onClick={() => setShowWithdrawModal(true)}
                fullWidth
              >
                <ArrowDownIcon className="w-4 h-4 mr-2 text-primary" />
                Withdraw
              </Button>
            </div>
          </div>
        </Card>

        {/* Deposit Address Card */}
        <Card className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div className="mb-4 md:mb-0">
              <h3 className={globalStyle.h5}>Deposit Address</h3>
              {depositAddress ? (
                <div className="mt-2">
                  <div className="flex items-center space-x-2 flex-wrap">
                    <code className="bg-gray-800 px-3 py-2 rounded-lg text-sm font-mono truncate max-w-[200px] sm:max-w-[400px] text-white">
                      {getAddressString()}
                    </code>
                    <button
                      onClick={() => copyToClipboard(getAddressString())}
                      className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-white"
                    >
                      <DocumentDuplicateIcon className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setShowQrModal(true)}
                      className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-white"
                    >
                      <QrCodeIcon className="w-5 h-5" />
                    </button>
                  </div>
                  <p className={`${globalStyle.caption} mt-2 text-gray-400`}>
                    {getDepositNote()}
                  </p>
                </div>
              ) : (
                <div>Loading deposit address</div>
              )}
            </div>
            <div>
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => dispatch(getDepositAddress())}
                loading={isLoading}
              >
                Refresh Address
              </Button>
            </div>
          </div>
        </Card>

        {/* Transactions Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
          <h2 className={globalStyle.h3}>Transaction History</h2>
          <div className="flex space-x-3 mt-4 md:mt-0">
            <Button
              variant="tertiary"
              size="sm"
              onClick={() => setShowFilters(true)}
            >
              <FunnelIcon className="w-4 h-4 mr-2" />
              Filters
            </Button>
            <Button
              variant="tertiary"
              size="sm"
              onClick={handleRefresh}
              loading={isLoading}
            >
              Refresh
            </Button>
          </div>
        </div>

        {/* Transactions List */}
        <Card padding="lg">
          {error && <Alert type="error" message={error} className="mb-6" />}
          {isLoading ? (
            <LoadingScreen message="Loading transactions..." />
          ) : filteredTransactions.length === 0 ? (
            <div className="text-center py-12">
              <MagnifyingGlassIcon className="w-12 h-12 text-gray-500 mx-auto mb-4" />
              <h3 className={globalStyle.h5}>No Transactions Found</h3>
              <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
                {transactions.length === 0
                  ? "You haven't made any transactions yet."
                  : "No transactions match your filters."}
              </p>
              {transactions.length === 0 && (
                <Button
                  className="mt-4"
                  onClick={() => setShowDepositModal(true)}
                >
                  Make Your First Deposit
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className={globalStyle.table}>
                  <thead className={globalStyle.tableHead}>
                    <tr>
                      <th className={globalStyle.tableHeadCell}>Type</th>
                      <th className={globalStyle.tableHeadCell}>Amount</th>
                      <th className={globalStyle.tableHeadCell}>Status</th>
                      <th className={globalStyle.tableHeadCell}>Date</th>
                      <th className={globalStyle.tableHeadCell}>Details</th>
                    </tr>
                  </thead>
                  <tbody className={globalStyle.tableBody}>
                    {filteredTransactions.map((transaction) => (
                      <tr
                        key={transaction.id}
                        className={globalStyle.tableRowHover}
                      >
                        <td className={globalStyle.tableCell}>
                          <div className="flex items-center">
                            {getTypeIcon(transaction.type)}
                            <div className="ml-2">
                              <span>{getTypeLabel(transaction.type)}</span>
                              {transaction.type === "team_bonus" && (
                                <p className="text-xs text-gray-400 mt-0.5">
                                  {getTeamBonusSubLabel(transaction)}
                                </p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className={globalStyle.tableCell}>
                          <div>
                            <div
                              className={`font-medium ${getTypeColor(transaction.type)}`}
                            >
                              {transaction.type === "withdrawal" ||
                              transaction.type === "bot_purchase"
                                ? "-"
                                : "+"}
                              {formatXRPAmount(transaction.amount)} XRP
                            </div>
                            <div className="text-sm text-gray-400">
                              {formatCurrency(transaction.amount * xrpPrice)}
                            </div>
                          </div>
                        </td>
                        <td className={globalStyle.tableCell}>
                          {getStatusBadge(transaction.status || "pending")}
                        </td>
                        <td className={globalStyle.tableCell}>
                          {safeFormatDate(transaction.created_at)}
                        </td>
                        <td className={globalStyle.tableCell}>
                          <Button
                            variant="tertiary"
                            size="sm"
                            onClick={() =>
                              setShowTransactionDetail(transaction)
                            }
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-800 pt-4 mt-4">
                  <div className="text-sm text-gray-400">
                    Showing {filteredTransactions.length} of {totalItems}{" "}
                    transactions
                    {currentPage > 0 && totalPages > 0 && (
                      <span>
                        {" "}
                        · Page {currentPage} of {totalPages}
                      </span>
                    )}
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="tertiary"
                      size="sm"
                      disabled={currentPage === 1}
                      onClick={handlePreviousPage}
                    >
                      <ChevronLeftIcon className="w-4 h-4 mr-1" />
                      Previous
                    </Button>
                    <Button
                      variant="tertiary"
                      size="sm"
                      disabled={currentPage === totalPages}
                      onClick={handleNextPage}
                    >
                      Next
                      <ChevronRightIcon className="w-4 h-4 ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </Card>
      </main>

      <BottomNav />

      {/* ── Deposit Modal ─────────────────────────────────────────────────────── */}
      <Modal
        isOpen={showDepositModal}
        onClose={() => {
          setShowDepositModal(false);
          setDepositAmount("");
          setDepositTxId("");
          setValidationErrors({});
        }}
        title="Deposit XRP"
        size="md"
      >
        <div className="space-y-6">
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Amount (XRP) *</label>
            <input
              type="number"
              min="5"
              max="1000000"
              step="0.000001"
              value={depositAmount}
              onChange={(e) => {
                setDepositAmount(e.target.value);
                setValidationErrors({});
              }}
              className={`${globalStyle.input} ${validationErrors.amount ? "border-red-500" : ""}`}
              placeholder="Enter amount"
            />
            {validationErrors.amount && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.amount}
              </p>
            )}
            <p className={`${globalStyle.caption} mt-2 text-gray-400`}>
              Minimum: 5 XRP, Maximum: 1,000,000 XRP
              <br />
              Value:{" "}
              {depositAmount
                ? formatCurrency(parseFloat(depositAmount) * xrpPrice)
                : formatCurrency(0)}
            </p>
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Transaction ID *</label>
            <input
              type="text"
              value={depositTxId}
              onChange={(e) => {
                setDepositTxId(e.target.value);
                setValidationErrors({});
              }}
              className={`${globalStyle.input} ${validationErrors.txId ? "border-red-500" : ""}`}
              placeholder="Enter transaction ID from your wallet"
            />
            {validationErrors.txId && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.txId}
              </p>
            )}
          </div>
          <div className="bg-yellow-900 border border-yellow-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-yellow-300 mb-2">
              Important Instructions
            </h4>
            <ul className="text-sm text-yellow-200 space-y-1">
              <li>1. Send XRP to your deposit address first</li>
              <li>2. Wait for confirmation on the blockchain</li>
              <li>3. Enter the transaction ID here</li>
              <li>4. Processing may take 24-48 hours</li>
            </ul>
          </div>
          <div className="flex space-x-3">
            <Button
              variant="tertiary"
              onClick={() => {
                setShowDepositModal(false);
                setDepositAmount("");
                setDepositTxId("");
                setValidationErrors({});
              }}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeposit}
              loading={depositLoading}
              disabled={!depositAmount || !depositTxId}
              fullWidth
            >
              Submit Deposit Request
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── Withdrawal Modal ──────────────────────────────────────────────────── */}
      <Modal
        isOpen={showWithdrawModal}
        onClose={() => {
          setShowWithdrawModal(false);
          setWithdrawAmount("");
          setWithdrawAddress("");
          setWithdrawMemo("");
          setValidationErrors({});
        }}
        title="Withdraw XRP"
        size="md"
      >
        <div className="space-y-6">
          {/* Day-of-week status banner */}
          {withdrawalRules &&
            (!isWithdrawalAllowedToday ? (
              <div className="rounded-lg p-4 bg-red-900/30 border border-red-800">
                <div className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="w-5 h-5 mt-0.5 flex-shrink-0 text-red-400" />
                  <div>
                    <p className="text-sm font-medium text-red-300">
                      Withdrawals are closed today
                    </p>
                    <p className="text-xs mt-1 text-red-400">
                      Next open:{" "}
                      <span className="font-medium text-white">
                        {getNextAllowedDay()}
                      </span>
                    </p>
                    <p className="text-xs mt-1 text-red-400">
                      Allowed days:{" "}
                      {withdrawalRules.allowed_days
                        .map((d) => DAY_NAMES[d])
                        .join(", ")}
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="rounded-lg p-4 bg-green-900/30 border border-green-800">
                <div className="flex items-start gap-3">
                  <CalendarDaysIcon className="w-5 h-5 mt-0.5 flex-shrink-0 text-green-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-green-300">
                      Withdrawals are open today
                    </p>
                    <p className="text-xs mt-1 text-green-400">
                      Remaining today:{" "}
                      <span className="font-medium text-white">
                        {formatXRP(withdrawalRules.remaining_today)} XRP
                      </span>
                    </p>
                    <div className="flex flex-wrap gap-3 mt-1.5">
                      <span className="text-xs text-gray-400">
                        Activity limit:{" "}
                        <span className="text-gray-200">
                          {formatXRP(
                            withdrawalRules.base_limit ??
                              withdrawalRules.daily_limit,
                          )}{" "}
                          XRP
                        </span>
                      </span>
                      {(withdrawalRules.bonus_top_up ?? 0) > 0 && (
                        <span className="text-xs text-yellow-400">
                          + Level bonus:{" "}
                          {formatXRP(withdrawalRules.bonus_top_up!)} XRP
                          <span className="text-gray-500 ml-1">
                            ({withdrawalRules.level_bonus_pct}% of{" "}
                            {formatXRP(withdrawalRules.available_bonus ?? 0)}{" "}
                            available)
                          </span>
                        </span>
                      )}
                      <span className="text-xs text-gray-500">
                        Used: {formatXRP(withdrawalRules.withdrawn_today)} XRP
                      </span>
                    </div>
                    {/* Timezone pill — lets user know the schedule is in their local time */}
                    <div className="flex items-center gap-1 mt-2">
                      <GlobeAltIcon className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-xs text-gray-400">
                        Schedule is in your local time:{" "}
                        <span className="text-gray-300">
                          {tzLabel(withdrawalRules.user_timezone ?? BROWSER_TZ)}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}

          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Amount (XRP) *</label>
            <input
              type="number"
              min="5"
              max={effectiveMaxWithdraw}
              step="0.000001"
              value={withdrawAmount}
              onChange={(e) => {
                setWithdrawAmount(e.target.value);
                setValidationErrors({});
              }}
              className={`${globalStyle.input} ${validationErrors.amount ? "border-red-500" : ""}`}
              placeholder="Enter amount"
            />
            {validationErrors.amount && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.amount}
              </p>
            )}
            <p className={`${globalStyle.caption} mt-2 text-gray-400`}>
              Minimum: 5 XRP · Maximum:{" "}
              {formatXRP(withdrawalRules?.remaining_today ?? 50000)} XRP today
              <br />
              Available balance: {formatBalance(balance?.xrp_balance)}
              <br />
              Value:{" "}
              {withdrawAmount
                ? formatCurrency(parseFloat(withdrawAmount) * xrpPrice)
                : formatCurrency(0)}
            </p>
          </div>

          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>Destination Address *</label>
            <input
              type="text"
              value={withdrawAddress}
              onChange={(e) => {
                setWithdrawAddress(e.target.value);
                setValidationErrors({});
              }}
              className={`${globalStyle.input} ${validationErrors.address ? "border-red-500" : ""}`}
              placeholder="Enter XRP address starting with 'r'"
            />
            {validationErrors.address && (
              <p className="text-red-500 text-sm mt-1">
                {validationErrors.address}
              </p>
            )}
          </div>

          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>
              Memo / Destination Tag (Optional)
            </label>
            <input
              type="text"
              value={withdrawMemo}
              onChange={(e) => setWithdrawMemo(e.target.value)}
              className={globalStyle.input}
              placeholder="Enter memo or destination tag if required"
            />
          </div>

          <div className="bg-blue-900 border border-blue-800 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-300 mb-2">
              🔐 Security Notice
            </h4>
            <ul className="text-sm text-blue-200 space-y-1">
              <li>• An OTP will be sent to your email for confirmation</li>
              <li>• OTP expires in 10 minutes</li>
              <li>• Never share your OTP with anyone</li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="tertiary"
              onClick={() => {
                setShowWithdrawModal(false);
                setWithdrawAmount("");
                setWithdrawAddress("");
                setWithdrawMemo("");
                setValidationErrors({});
              }}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              onClick={handleInitiateWithdrawal}
              disabled={
                !isWithdrawalAllowedToday ||
                !withdrawAmount ||
                !withdrawAddress ||
                parseFloat(withdrawAmount) > (balance?.xrp_balance || 0) ||
                parseFloat(withdrawAmount) < 5 ||
                (withdrawalRules
                  ? parseFloat(withdrawAmount) > withdrawalRules.remaining_today
                  : false)
              }
              fullWidth
            >
              {isWithdrawalAllowedToday
                ? "Continue with OTP Verification"
                : `Opens ${getNextAllowedDay()}`}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── OTP Modal ─────────────────────────────────────────────────────────── */}
      <Modal
        isOpen={showOtpModal}
        onClose={() => {}}
        title="Verify Withdrawal"
        size="sm"
      >
        <div className="space-y-6">
          {pendingWithdrawal && (
            <div className="bg-gray-800 rounded-lg p-4">
              <p className="text-sm text-gray-400 mb-2">Withdrawal Details:</p>
              <p className="text-white font-medium mb-1">
                Amount: {formatXRP(parseFloat(pendingWithdrawal.amount))} XRP
              </p>
              <p className="text-white text-sm break-all">
                To: {pendingWithdrawal.address}
              </p>
            </div>
          )}
          <div className="text-center">
            <div className="w-16 h-16 rounded-full bg-blue-900 flex items-center justify-center mx-auto mb-4">
              <ShieldCheckIcon className="w-8 h-8 text-blue-300" />
            </div>
            <h3 className={globalStyle.h5}>Enter Verification Code</h3>
            <p className={`${globalStyle.body2} mt-2 text-gray-400`}>
              We've sent a 6-digit OTP to your email address.
            </p>
          </div>
          <div className={globalStyle.formGroup}>
            <label className={globalStyle.label}>6-Digit OTP *</label>
            <div className="flex justify-center space-x-2">
              {withdrawalOtp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-${index}`}
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) =>
                    handleOtpChange(index, e.target.value.replace(/\D/g, ""))
                  }
                  onKeyDown={(e) => handleOtpKeyDown(index, e)}
                  className="w-12 h-12 text-center text-2xl font-semibold border-2 border-gray-700 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-20 transition-all duration-300 bg-gray-900 text-white"
                />
              ))}
            </div>
            {validationErrors.otp && (
              <p className="text-red-500 text-sm mt-2 text-center">
                {validationErrors.otp}
              </p>
            )}
            <div className="flex items-center justify-between mt-4">
              <p className={`${globalStyle.caption} text-gray-400`}>
                ⏱️ Expires in: {formatTime(otpTimer)}
              </p>
              <button
                onClick={handleResendOtp}
                disabled={otpTimer > 0}
                className={`text-sm ${otpTimer > 0 ? "text-gray-500 cursor-not-allowed" : "text-primary hover:text-primary-300"}`}
              >
                Resend OTP
              </button>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="tertiary"
              onClick={handleCancelWithdrawal}
              fullWidth
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmWithdrawal}
              disabled={withdrawalOtp.join("").length !== 6 || isOtpLoading}
              loading={isOtpLoading}
              fullWidth
            >
              Verify & Withdraw
            </Button>
          </div>
        </div>
      </Modal>

      {/* ── QR Code Modal ─────────────────────────────────────────────────────── */}
      <Modal
        isOpen={showQrModal}
        onClose={() => setShowQrModal(false)}
        title="Scan QR Code"
        size="sm"
      >
        <div className="text-center py-6">
          {getAddressString() ? (
            <>
              <div className="bg-gray-800 p-6 rounded-lg inline-block">
                {qrCodeUrl ? (
                  <img
                    src={qrCodeUrl}
                    alt="QR Code"
                    className="w-48 h-48"
                    onError={(e) => {
                      const t = e.target as HTMLImageElement;
                      t.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="w-48 h-48 bg-black flex items-center justify-center border-2 border-dashed border-gray-700 rounded-lg">
                    <QrCodeIcon className="w-24 h-24 text-gray-500" />
                  </div>
                )}
              </div>
              <p className={`${globalStyle.caption} mt-4 text-gray-400`}>
                Scan this QR code with your wallet app
              </p>
              <div className="mt-4 p-3 bg-gray-800 rounded-lg">
                <p className="text-sm font-mono break-all text-white">
                  {getAddressString()}
                </p>
              </div>
              <div className="mt-4 flex justify-center">
                <Button onClick={() => copyToClipboard(getAddressString())}>
                  <DocumentDuplicateIcon className="w-4 h-4 mr-2" />
                  Copy Address
                </Button>
              </div>
            </>
          ) : (
            <LoadingScreen message="Loading QR code..." />
          )}
        </div>
      </Modal>

      {/* ── Filters Drawer ────────────────────────────────────────────────────── */}
      <Drawer
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        title="Filter Transactions"
        position="right"
        size="sm"
      >
        <div className="space-y-6">
          <div>
            <h4 className={`${globalStyle.h6} mb-3`}>Transaction Type</h4>
            <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2">
              {getTransactionTypeOptions().map((type) => (
                <label key={type} className="flex items-center">
                  <input
                    type="radio"
                    name="transactionType"
                    checked={transactionType === type}
                    onChange={() => setTransactionType(type)}
                    className="w-4 h-4 text-primary border-gray-600 focus:ring-primary bg-gray-800"
                  />
                  <span className="ml-2 capitalize text-white">
                    {type === "all" ? "All Types" : getTypeLabel(type)}
                  </span>
                </label>
              ))}
            </div>
          </div>
          <div>
            <h4 className={`${globalStyle.h6} mb-3`}>Status</h4>
            <div className="space-y-2">
              {(["all", "pending", "completed", "rejected"] as const).map(
                (status) => (
                  <label key={status} className="flex items-center">
                    <input
                      type="radio"
                      name="transactionStatus"
                      checked={transactionStatus === status}
                      onChange={() => setTransactionStatus(status)}
                      className="w-4 h-4 text-primary border-gray-600 focus:ring-primary bg-gray-800"
                    />
                    <span className="ml-2 capitalize text-white">
                      {status === "all" ? "All Statuses" : status}
                    </span>
                  </label>
                ),
              )}
            </div>
          </div>
          <Button onClick={() => setShowFilters(false)} fullWidth>
            Apply Filters
          </Button>
          <Button
            variant="tertiary"
            onClick={() => {
              setTransactionType("all");
              setTransactionStatus("all");
              setShowFilters(false);
            }}
            fullWidth
          >
            Clear Filters
          </Button>
        </div>
      </Drawer>

      {/* ── Transaction Detail Modal ───────────────────────────────────────────── */}
      <Modal
        isOpen={!!showTransactionDetail}
        onClose={() => setShowTransactionDetail(null)}
        title="Transaction Details"
        size="md"
      >
        {showTransactionDetail && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>
                  Transaction Type
                </h4>
                <div className="flex items-center mt-1">
                  {getTypeIcon(showTransactionDetail.type)}
                  <div className="ml-2">
                    <p className="font-medium capitalize text-white">
                      {getTypeLabel(showTransactionDetail.type)}
                    </p>
                    {showTransactionDetail.type === "team_bonus" && (
                      <p className="text-xs text-gray-400 mt-0.5">
                        {getTeamBonusSubLabel(showTransactionDetail)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>Status</h4>
                <div className="mt-1">
                  {getStatusBadge(showTransactionDetail.status || "pending")}
                </div>
              </div>
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>Amount</h4>
                <p
                  className={`${globalStyle.h4} ${showTransactionDetail.type === "withdrawal" || showTransactionDetail.type === "bot_purchase" ? "text-red-500" : "text-green-500"}`}
                >
                  {showTransactionDetail.type === "withdrawal" ||
                  showTransactionDetail.type === "bot_purchase"
                    ? "-"
                    : "+"}
                  {formatXRPAmount(showTransactionDetail.amount)} XRP
                </p>
                <p className="text-sm text-gray-400">
                  {formatCurrency(showTransactionDetail.amount * xrpPrice)}
                </p>
              </div>
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>Date</h4>
                <p className="text-white">
                  {safeFormatDate(showTransactionDetail.created_at)}
                </p>
              </div>
            </div>

            {showTransactionDetail.type === "withdrawal" && (
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>
                  Destination Address
                </h4>
                <div className="flex items-center space-x-2">
                  <code className="bg-gray-800 px-3 py-2 rounded-lg text-sm font-mono flex-1 break-all text-white">
                    {showTransactionDetail.destination_address}
                  </code>
                  <button
                    onClick={() => {
                      if (showTransactionDetail.destination_address)
                        copyToClipboard(
                          showTransactionDetail.destination_address,
                        );
                    }}
                    className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-white"
                  >
                    <DocumentDuplicateIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {showTransactionDetail.memo && (
              <div>
                <h4 className={`${globalStyle.h6} text-gray-400`}>
                  Memo / Destination Tag
                </h4>
                <div className="flex items-center space-x-2">
                  <code className="bg-gray-800 px-3 py-2 rounded-lg text-sm font-mono flex-1 break-all text-white">
                    {showTransactionDetail.memo}
                  </code>
                  <button
                    onClick={() => copyToClipboard(showTransactionDetail.memo!)}
                    className="p-2 hover:bg-gray-800 rounded-lg transition-colors text-white"
                  >
                    <DocumentDuplicateIcon className="w-5 h-5" />
                  </button>
                </div>
              </div>
            )}

            {["deposit", "withdrawal"].includes(showTransactionDetail.type) &&
              showTransactionDetail.tx_hash && (
                <div className="pt-4 border-t border-gray-800">
                  <Button
                    fullWidth
                    onClick={() =>
                      window.open(
                        `https://livenet.xrpl.org/transactions/${showTransactionDetail.tx_hash}`,
                        "_blank",
                      )
                    }
                  >
                    View on XRP Explorer
                  </Button>
                </div>
              )}

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-800">
              <Button
                variant="tertiary"
                onClick={() => setShowTransactionDetail(null)}
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Wallet;
